import { type FC, useState } from 'react'
import { FiTrash2 } from 'react-icons/fi'
import { useRevalidator } from 'react-router-dom'

import type { ButtonProps } from '@app/components/ui/button'
import { Button } from '@app/components/ui/button'
import { toaster } from '@app/components/ui/toaster'
import ConfirmPopover from '@app/shared/confirmPopover'
import { useStore } from '@app/store'

interface Props {
  metricId?: string
  onDelete?: () => void
  children?: FC<ButtonProps>
}

const DefaultChild: FC<ButtonProps> = (props) => (
  <Button color="error" aria-label="Delete metric" variant="outline" {...props}>
    <FiTrash2 />
    Delete
  </Button>
)

const MetricDeleteButton: FC<Props> = ({ metricId = null, onDelete = () => {}, children = DefaultChild }) => {
  const revalidator = useRevalidator()
  const deleteObject = useStore.use.deleteObject()
  const [loading, setLoading] = useState(false)
  const Child = children || DefaultChild

  const deleteMetric = async () => {
    if (!metricId) {
      return false
    }

    setLoading(true)

    try {
      const result = await deleteObject({ id: metricId, classType: 'metric' }, { updatePageCounts: true })
      toaster.create({ title: 'Deleted metric' })
      onDelete()
      revalidator.revalidate()
      return result
    } finally {
      setLoading(false)
    }
  }

  return (
    <ConfirmPopover
      onConfirm={deleteMetric}
      header="Delete metric"
      body="Are you sure? You can&#39;t undo this action afterwards."
      confirmButtonText="Delete"
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <Child loading={loading} />
    </ConfirmPopover>
  )
}

export default MetricDeleteButton
